import React, { useMemo } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { ActivityState } from "@axio/graphql/generated/graphql";

import CheckedBadge from "../../svgs/common/CheckedBadge";
import DueDatePill from "../Shared/DueDatePill";
import ScheduledPill from "../Shared/ScheduledPill";

function AxioResourceCard({
  title,
  icon,
  videoUrl,
  files,
  isHovered,
  onPress,
  onHoverIn,
  onHoverOut,
  dueDate,
  activityState,
  scheduledDate,
  isPending,
  grade,
}) {
  const getResourceTypeText = () => {
    const hasVideo = !!videoUrl;
    const hasFiles = files && files.length > 0;

    if (hasVideo && hasFiles) return "Files & Video";
    if (hasVideo) return "Video";
    if (hasFiles) return "Files";
    return null;
  };

  const resourceType = getResourceTypeText();

  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  // hide if draft
  if (activityState === ActivityState.Draft) {
    return null;
  }

  const renderStatus = () => {
    if (isModuleLocked) return null;

    if (isPending) {
      return (
        <View style={styles.gradeTagUngraded}>
          <CustomText
            text="Evaluation Pending"
            size="s"
            weight="ultraBold"
            style={{ color: "#fff" }}
            useTranslationText={false}
          />
        </View>
      );
    }

    if (grade != null) {
      return (
        <View style={styles.gradeContainer}>
          <View style={styles.gradeTag}>
            <CustomText
              text={`${grade}`}
              size="s"
              weight="ultraBold"
              style={{ color: "#fff" }}
              useTranslationText={false}
            />
          </View>
          <CheckedBadge />
        </View>
      );
    }

    return null;
  };

  return (
    <Pressable
      onPress={isModuleLocked ? undefined : onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={`Open ${title} card`}
      accessibilityRole="button"
      style={({ pressed }) => [
        styles.resourceItem,
        !isModuleLocked && isHovered && styles.resourceItemHovered,
        pressed && styles.resourceItemPressed,
        isModuleLocked && styles.resourceItemLocked,
      ]}>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <View style={styles.titleRow}>
            <View style={styles.titleContent}>
              {icon && <View style={styles.iconContainer}>{icon}</View>}
              <CustomText
                text={title}
                weight="bold"
                style={[styles.resourceItemTitle, isModuleLocked && styles.resourceItemTitleLocked]}
                useTranslationText={false}
                aria-level="3"
                role="heading"
                numberOfLines={1}
                ellipsizeMode="tail"
              />
            </View>
            {!isModuleLocked && <DueDatePill dueDate={dueDate} />}
            {isModuleLocked && <ScheduledPill scheduledDate={scheduledDate} />}
          </View>

          {resourceType && (
            <CustomText
              text={resourceType}
              style={styles.resourceItemDescription}
              useTranslationText={false}
              numberOfLines={1}
            />
          )}
        </View>
      </View>
      <View style={styles.statusContainer}>{renderStatus()}</View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  resourceItemLeft: {
    flex: 1,
    flexDirection: "column",
    gap: 4,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  gradeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  gradeTag: {
    padding: 8,
    backgroundColor: "#0084FF",
    borderRadius: 100,
  },
  gradeTagUngraded: {
    padding: 8,
    backgroundColor: "#D0D5DD",
    borderRadius: 100,
  },
  resourceItem: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  resourceItemLocked: {
    cursor: "auto",
  },
  resourceItemHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  resourceItemPressed: {
    backgroundColor: "#F5F5F5",
  },
  textContainer: {
    flex: 1,
    gap: 12,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
  },
  titleContent: {
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
  },
  iconContainer: {
    marginRight: 10,
  },
  resourceItemTitle: {
    alignSelf: "center",
    flexShrink: 1,
    minWidth: 0,
  },
  resourceItemTitleLocked: {
    opacity: 0.5,
  },
  resourceItemDescription: {
    fontSize: 12,
    lineHeight: 16,
    color: "#667085",
  },
});

export default AxioResourceCard;
